import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useEffect, useMemo } from 'react'

import { CacheKeys } from '../lib/cache'
import { type PaginatedResults, toCamelCase, toSnakeCase } from '../lib/typeHelper'

export enum ClaimSource {
  WEB = 'WEB',
  SMS = 'SMS',
  PHONE = 'PHONE',
}

export enum ClaimStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  /** Both are legacy: neither is offered to staff or drawn on the timeline. */
  IN_REVIEW = 'IN_REVIEW',
  INFO_NEEDED = 'INFO_NEEDED',
  FILED = 'FILED',
  OPEN_WITH_CARRIER = 'OPEN_WITH_CARRIER',
  CLOSED = 'CLOSED',
}

export enum ClaimOutcome {
  PAID = 'PAID',
  DENIED = 'DENIED',
  CLOSED_NO_PAYMENT = 'CLOSED_NO_PAYMENT',
  WITHDRAWN = 'WITHDRAWN',
}

export enum ReporterRelationship {
  POLICYHOLDER_OWNER = 'POLICYHOLDER_OWNER',
  DRIVER = 'DRIVER',
  PASSENGER = 'PASSENGER',
  FLEET_MANAGER = 'FLEET_MANAGER',
  REPORTER_ONLY = 'REPORTER_ONLY',
}

/** Uploads are no longer categorised by the reporter — new ones land in OTHER. */
export enum ClaimPhotoCategory {
  OVERALL_SCENE = 'CLAIM_PHOTO_OVERALL_SCENE',
  VEHICLE_DAMAGE = 'CLAIM_PHOTO_VEHICLE_DAMAGE',
  ROADWAY = 'CLAIM_PHOTO_ROADWAY',
  OTHER_DRIVER_PLATE = 'CLAIM_PHOTO_OTHER_DRIVER_PLATE',
  OTHER = 'CLAIM_PHOTO_OTHER',
}

export interface ClaimReporterData {
  name: string
  phone: string
  relationships: ReporterRelationship[]
}

// --- Step 3: incident details ---

export enum ClaimWhatHappened {
  TRUCK_HIT_VEHICLE = 'TRUCK_HIT_VEHICLE',
  VEHICLE_HIT_TRUCK = 'VEHICLE_HIT_TRUCK',
  ROLLOVER = 'ROLLOVER',
  HIT_OBJECT = 'HIT_OBJECT',
  CARGO_DAMAGED_LOST = 'CARGO_DAMAGED_LOST',
  INJURY = 'INJURY',
  THEFT_VANDALISM = 'THEFT_VANDALISM',
  FIRE_EXPLOSION = 'FIRE_EXPLOSION',
  WEATHER = 'WEATHER',
  OTHER = 'OTHER',
}

export enum ClaimHowManyVehicles {
  TWO_OR_MORE = 'TWO_OR_MORE',
  MY_VEHICLE_ONLY = 'MY_VEHICLE_ONLY',
  NO_VEHICLES = 'NO_VEHICLES',
}

export enum ClaimWhatDamaged {
  MY_TRACTOR = 'MY_TRACTOR',
  MY_TRAILER = 'MY_TRAILER',
  OTHER_VEHICLE = 'OTHER_VEHICLE',
  PROPERTY = 'PROPERTY',
  MY_CARGO = 'MY_CARGO',
  PERSONAL_VEHICLE = 'PERSONAL_VEHICLE',
}

export enum ClaimTruckDriveable {
  YES = 'YES',
  NO = 'NO',
  UNSURE = 'UNSURE',
}

export enum ClaimTowVehicle {
  NONE = 'NONE',
  TRACTOR = 'TRACTOR',
  TRAILER = 'TRAILER',
  OTHER_VEHICLE = 'OTHER_VEHICLE',
}

export enum ClaimLoadStatus {
  NO_LOAD = 'NO_LOAD',
  SECURED = 'SECURED',
  NOT_SECURED = 'NOT_SECURED',
}

export interface ClaimIncidentData {
  date: string | null
  time: string | null
  city: string
  state: string
  locationDescription: string
  otherPartyPlate: string
  whatHappened: ClaimWhatHappened[] | null
  howManyVehicles: string
  whatDamaged: ClaimWhatDamaged[] | null
  briefDescription: string
  cargoOnBoard: boolean | null
  freightBrokerName: string
  freightBrokerContact: string
  truckDriveable: string
  vehiclesNeedingTow: ClaimTowVehicle[] | null
  loadStatus: string
  anythingElse: string
}

export interface ClaimPhotoAttachment {
  id: string
  category: ClaimPhotoCategory
  fileName: string
  fileUrl: string | null
}

// --- Step 2: truck / trailer / driver ---

export enum ClaimTrailerOwnership {
  OWNED_NOT_LISTED = 'OWNED_NOT_LISTED',
  LEASED = 'LEASED',
  THIRD_PARTY_CARRIER = 'THIRD_PARTY_CARRIER',
  UNKNOWN = 'UNKNOWN',
}

export enum ClaimDriverRelationship {
  EMPLOYEE = 'EMPLOYEE',
  OWNER_INSURED = 'OWNER_INSURED',
  CONTRACTOR = 'CONTRACTOR',
  OTHER = 'OTHER',
}

export interface ClaimTruck {
  truckId: string | null
  vin: string
  unitNumber: string
  licensePlate: string
  licenseState: string
  year: number | null
  make: string
  model: string
}

export interface ClaimTrailer {
  trailerId: string | null
  vin: string
  unitNumber: string
  licensePlate: string
  model: string
  trailerOwnership: string
}

export interface ClaimDriverInfo {
  driverId: string | null
  firstName: string
  lastName: string
  cdlNumber: string
  cdlState: string
  relationshipToInsured: string
  wasOnDuty: boolean | null
}

// --- Step 4: others involved (injuries / other vehicles / police) ---

export enum ClaimInjuredParty {
  OUR_DRIVER = 'OUR_DRIVER',
  OUR_PASSENGER = 'OUR_PASSENGER',
  OTHER_DRIVER = 'OTHER_DRIVER',
  OTHER_PASSENGER = 'OTHER_PASSENGER',
  PEDESTRIAN_CYCLIST = 'PEDESTRIAN_CYCLIST',
}

export enum ClaimCitationIssued {
  NONE = 'NONE',
  OUR_DRIVER = 'OUR_DRIVER',
  OTHER_DRIVER = 'OTHER_DRIVER',
  UNKNOWN = 'UNKNOWN',
}

export interface ClaimInjuriesData {
  numPassengers: number
  anyInjuries: boolean | null
  injuredParties: ClaimInjuredParty[] | null
  numInjured: number | null
  transportedToHospital: boolean | null
}

export interface ClaimPoliceData {
  policeContacted: boolean | null
  policeResponded: boolean | null
  reportFiled: boolean | null
  reportCaseNumber: string
  jurisdiction: string
  citationIssued: string
}

export interface ClaimOtherVehicle {
  licensePlate: string
  otherDriverName: string
  insuranceCompany: string
  policyNumber: string
  contactInfo: string
}

/** Stage -> what staff want the customer to read on that step of the timeline. */
export type ClaimStatusNotes = Partial<Record<ClaimStatus, string>>

/** Reading the Step 1 uploads to pre-fill the later steps; '' when never asked. */
export enum ClaimPhotoExtractionStatus {
  PENDING = 'PENDING',
  DONE = 'DONE',
  FAILED = 'FAILED',
}

export interface Claim {
  id: string
  idShort: string
  source: ClaimSource
  status: ClaimStatus
  reporter: ClaimReporterData | null
  incident: ClaimIncidentData | null
  photos: ClaimPhotoAttachment[]
  photoUploadDeferred: boolean
  photoDeferralReason: string
  photoExtractionStatus: ClaimPhotoExtractionStatus | ''
  /** Fields pre-filled from the uploads, e.g. 'truck' or 'incident.city'. */
  photoExtractionFilled: string[]
  trailerAttached: boolean | null
  truck: ClaimTruck | null
  trailer: ClaimTrailer | null
  driver: ClaimDriverInfo | null
  otherVehiclesInvolved: boolean | null
  injuries: ClaimInjuriesData | null
  police: ClaimPoliceData | null
  otherVehicle: ClaimOtherVehicle | null
  certifyTrue: boolean
  authorizeInvestigate: boolean
  signature: string
  submissionDate: string | null
  submittedAt: string | null
  filedAt: string | null
  carrierOpenedAt: string | null
  closedAt: string | null
  outcome: ClaimOutcome | null
  carrierReference: string
  adjusterName: string
  adjusterContact: string
  adjusterAssignedAt: string | null
  submittedByName: string
  statusNotes: ClaimStatusNotes
  dateCreated: string
  dateUpdated: string
}

export interface PublicClaim {
  id: string
  idShort: string
  status: ClaimStatus
  photos: ClaimPhotoAttachment[]
  photoUploadDeferred: boolean
}

const API = '/api/v1/insure/claims'
const PUBLIC_API = '/api/v1/insure/public/claims'
const INTERNAL_API = '/api/v1/insure/internal/claims'

/** On-behalf context: a CAI user can create/edit a claim for a business owner. */
export interface OnBehalfClaimParams {
  orgId?: string
  userId?: string
}

export function createDraftClaim(ctx?: OnBehalfClaimParams): Promise<Claim> {
  return axios
    .post(`${API}/`, {}, { params: { org_id: ctx?.orgId, user_id: ctx?.userId } })
    .then((r) => toCamelCase(r.data) as Claim)
}

export function useCreateDraftClaim() {
  return useMutation({ mutationFn: createDraftClaim })
}

export function fetchClaim(id: string, orgId?: string): Promise<Claim> {
  return axios.get(`${API}/${id}/`, { params: { org_id: orgId } }).then((r) => toCamelCase(r.data) as Claim)
}

export function useClaim(id: string | undefined, orgId?: string) {
  return useQuery({
    queryKey: [CacheKeys.CLAIMS, id, orgId],
    queryFn: () => fetchClaim(id!, orgId),
    enabled: !!id,
  })
}

export interface PatchClaimData {
  reporter?: Partial<ClaimReporterData>
  incident?: Partial<ClaimIncidentData>
  photoUploadDeferred?: boolean
  photoDeferralReason?: string
  trailerAttached?: boolean
  truck?: Partial<ClaimTruck>
  trailer?: Partial<ClaimTrailer> | null
  driver?: Partial<ClaimDriverInfo>
  otherVehiclesInvolved?: boolean
  injuries?: Partial<ClaimInjuriesData>
  police?: Partial<ClaimPoliceData>
  otherVehicle?: Partial<ClaimOtherVehicle> | null
}

export function patchClaimRequest(id: string, data: PatchClaimData, orgId?: string): Promise<Claim> {
  return axios
    .patch(`${API}/${id}/`, toSnakeCase(data), { params: { org_id: orgId } })
    .then((r) => toCamelCase(r.data) as Claim)
}

export interface ClaimPhotoExtractionProgress {
  photoExtractionStatus: ClaimPhotoExtractionStatus | ''
  photoExtractionFilled: string[]
}

/** Just how the upload read is going — cheap to poll, unlike the full claim. */
export function fetchClaimPhotoExtraction(id: string, orgId?: string): Promise<ClaimPhotoExtractionProgress> {
  return axios
    .get(`${API}/${id}/photo-extraction/`, { params: { org_id: orgId } })
    .then((r) => toCamelCase(r.data) as ClaimPhotoExtractionProgress)
}

/** Queue a read of the new uploads; `capturedAt` (local EXIF time) is a fallback incident time. */
export function requestClaimPhotoExtraction(id: string, orgId?: string, capturedAt?: string): Promise<Claim> {
  return axios
    .post(`${API}/${id}/extract-photos/`, toSnakeCase({ capturedAt }), { params: { org_id: orgId } })
    .then((r) => toCamelCase(r.data) as Claim)
}

export interface SubmitClaimData {
  signature: string
  certifyTrue: boolean
  authorizeInvestigate: boolean
}

/** Finalize a claim: records certification + signature and marks it SUBMITTED. */
export function submitClaimRequest(id: string, data: SubmitClaimData, orgId?: string): Promise<Claim> {
  return axios
    .post(`${API}/${id}/submit/`, toSnakeCase(data), { params: { org_id: orgId } })
    .then((r) => toCamelCase(r.data) as Claim)
}

export function usePatchClaim(id: string | undefined, orgId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: PatchClaimData): Promise<Claim> => patchClaimRequest(id!, data, orgId),
    onSuccess: (claim) => qc.setQueryData([CacheKeys.CLAIMS, id], claim),
  })
}

function uploadPhoto(baseUrl: string, id: string, file: File, orgId?: string) {
  const formData = new FormData()
  formData.append('file', file)
  return axios.post(`${baseUrl}/${id}/photos/`, formData, { params: { org_id: orgId } }).then((r) => r.data)
}

/** Imperative single-file upload (used by the wizard right after it creates the draft). */
export function uploadClaimPhotoRequest(id: string, file: File, orgId?: string) {
  return uploadPhoto(API, id, file, orgId)
}

export function useUploadClaimPhoto(id: string | undefined, orgId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ file }: { file: File }) => uploadPhoto(API, id!, file, orgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.CLAIMS, id] }),
  })
}

// --- Public (phone/SMS photo page) ---

export function usePublicClaim(id: string | undefined) {
  return useQuery({
    queryKey: [CacheKeys.PUBLIC_CLAIM, id],
    queryFn: (): Promise<PublicClaim> =>
      axios.get(`${PUBLIC_API}/${id}/`).then((r) => toCamelCase(r.data) as PublicClaim),
    enabled: !!id,
  })
}

export function useUploadPublicClaimPhoto(id: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ file }: { file: File }) => uploadPhoto(PUBLIC_API, id!, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.PUBLIC_CLAIM, id] }),
  })
}

// --- List hooks (unchanged behavior, updated types) ---

export interface ClaimListItem {
  id: string
  idShort: string
  source: ClaimSource
  status: ClaimStatus
  dateCreated: string
  dateUpdated: string
}

function getClaims(page: number, orgId?: string): Promise<PaginatedResults<ClaimListItem>> {
  return axios
    .get(`${API}/`, { params: { page, org_id: orgId } })
    .then((r) => toCamelCase(r.data) as PaginatedResults<ClaimListItem>)
}

function parsePageParam(url: string | null): number | null {
  if (!url) return null
  const page = new URL(url, window.location.origin).searchParams.get('page')
  return page ? Number(page) : null
}

export function useClaims({ enabled = true, orgId }: { enabled?: boolean; orgId?: string } = {}) {
  const { data, fetchNextPage, hasNextPage, isFetching, refetch, isLoading } = useInfiniteQuery({
    queryKey: [CacheKeys.CLAIMS, 'list', orgId],
    queryFn: ({ pageParam = 1 }) => getClaims(pageParam, orgId),
    enabled,
    refetchOnWindowFocus: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => parsePageParam(lastPage.next),
  })
  const claims = useMemo(() => data?.pages.flatMap((p) => p.results) ?? [], [data])
  useEffect(() => {
    if (!isFetching && hasNextPage && enabled) fetchNextPage()
  }, [isFetching, hasNextPage, fetchNextPage, enabled])
  return { claims, refetch, isLoading, isFetching, hasNextPage, fetchNextPage }
}

// --- Internal claims list ---

export interface InternalClaim {
  id: string
  idShort: string
  source: ClaimSource
  status: ClaimStatus
  outcome: ClaimOutcome | null
  submissionDate: string | null
  submittedAt: string | null
  filedAt: string | null
  carrierOpenedAt: string | null
  closedAt: string | null
  carrierReference: string
  adjusterName: string
  adjusterContact: string
  adjusterAssignedAt: string | null
  statusNotes: ClaimStatusNotes
  dateCreated: string
  orgId: string
  orgName: string
}

function setStatus({ id, status, outcome }: { id: string; status: ClaimStatus; outcome?: ClaimOutcome }) {
  return axios
    .post(`${INTERNAL_API}/${id}/status/`, { status, ...(outcome ? { outcome } : {}) })
    .then((r) => toCamelCase(r.data) as InternalClaim)
}

function setStatusNote({ id, status, note }: { id: string; status: ClaimStatus; note: string }) {
  return axios
    .post(`${INTERNAL_API}/${id}/note/`, { status, note })
    .then((r) => toCamelCase(r.data) as InternalClaim)
}

function setAdjuster({
  id,
  name,
  contact,
  carrierReference,
  assignedAt,
}: {
  id: string
  name: string
  contact: string
  carrierReference?: string
  assignedAt?: string
}) {
  return axios
    .post(`${INTERNAL_API}/${id}/adjuster/`, {
      adjuster_name: name,
      adjuster_contact: contact,
      adjuster_assigned_at: assignedAt ?? null,
      ...(carrierReference !== undefined ? { carrier_reference: carrierReference } : {}),
    })
    .then((r) => toCamelCase(r.data) as InternalClaim)
}

function useClaimMutation<TArgs>(mutationFn: (args: TArgs) => Promise<InternalClaim>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: (saved) => {
      // Patched now, since a dialog reopened before the refetch lands would show stale values.
      queryClient.setQueriesData<PaginatedResults<InternalClaim>>(
        { queryKey: [CacheKeys.INTERNAL_CLAIMS] },
        (page) =>
          page && {
            ...page,
            results: page.results.map((row) => (row.id === saved.id ? { ...row, ...saved } : row)),
          }
      )
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.INTERNAL_CLAIMS] })
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.CLAIMS] })
    },
  })
}

export function useSetClaimStatus() {
  return useClaimMutation(setStatus)
}

export function useSetClaimStatusNote() {
  return useClaimMutation(setStatusNote)
}

export function useSetClaimAdjuster() {
  return useClaimMutation(setAdjuster)
}

/** The customer pulls a submitted claim back; it closes as withdrawn. */
export function useWithdrawClaim(orgId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .post(`${API}/${id}/withdraw/`, null, { params: orgId ? { org_id: orgId } : undefined })
        .then((r) => toCamelCase(r.data) as Claim),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.CLAIMS] })
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.INTERNAL_CLAIMS] })
    },
  })
}

interface UseInternalClaimsParams {
  page?: number
  status?: ClaimStatus
  dateFrom?: string
  dateTo?: string
  orgId?: string
}

function getInternalClaims(params: UseInternalClaimsParams): Promise<PaginatedResults<InternalClaim>> {
  const queryParams: Record<string, string> = {}
  if (params.page && params.page > 1) queryParams.page = String(params.page)
  if (params.orgId) queryParams.org_id = params.orgId
  if (params.status) queryParams.status = params.status
  if (params.dateFrom) queryParams.date_from = params.dateFrom
  if (params.dateTo) queryParams.date_to = params.dateTo

  return axios
    .get(`${INTERNAL_API}/`, { params: queryParams })
    .then((response) => toCamelCase(response.data) as PaginatedResults<InternalClaim>)
}

export function useInternalClaims(params: UseInternalClaimsParams = {}) {
  const { data, isLoading } = useQuery({
    queryKey: [
      CacheKeys.INTERNAL_CLAIMS,
      params.page,
      params.status,
      params.dateFrom,
      params.dateTo,
      params.orgId,
    ],
    queryFn: () => getInternalClaims(params),
  })
  return {
    claims: data?.results ?? [],
    count: data?.count ?? 0,
    hasNextPage: !!data?.next,
    hasPreviousPage: !!data?.previous,
    isLoading,
  }
}
